import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { AppDataGrid, type AppColumn } from "../../components/AppDataGrid";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";

/** One pickable variable + the unit chosen for it on the record (e.g. C2 · ug/ml).
 *  Clicking it appends `Variable(Unit)` to the formula. */
export interface ReagentFormulaVariable {
  variable: string;
  unit: string;
}

/**
 * The legacy "Result Correction Formula" builder — opened from the Formula field of
 * the Reagent Prep ▸ Calculation Editor detail (RegentPrepCalculationEditorViewController
 * `Formula_Execute`).
 *
 * Three panes, as in the legacy DashboardView: a Variable | Unit grid of the VARIABLES
 * defined on this record (C1/C2/V1/V2/W1 with the unit chosen for each, EXCLUDING the
 * approach's target variable), a one-column grid of the OPERATOR tokens from
 * dbo.ReagentOperator, and the formula itself, labelled beside its box.
 * Clicking a row of either grid APPENDS its text to the END of the formula — legacy has no
 * insert-at-caret and no token removal, so `V1(ul)=C2(%)*V2(ml)` is built click by
 * click. The textbox stays directly editable, the only way to correct a mistake
 * (also true of legacy).
 *
 * When the record has no formula yet the box is seeded `{target}({unit})=` (legacy
 * OnShowing), target = the Approach's last word. OK copies the built string back to
 * the record's Formula field; legacy performs NO validation of the string and never
 * parses or evaluates it, so this dialog deliberately invents no grammar.
 */
export function ReagentFormulaDialog({
  open,
  initialText,
  seedPrefix,
  variables,
  operatorsUrl,
  onCancel,
  onSave,
}: {
  open: boolean;
  /** The record's stored Formula ('' when it has none). */
  initialText: string;
  /** `{target}({unit})=` seeded when there is no stored formula; '' to seed nothing. */
  seedPrefix: string;
  /** The non-target variables with their chosen unit. */
  variables: ReagentFormulaVariable[];
  /** Endpoint returning the operator tokens as a `string[]` under `{ data }`. */
  operatorsUrl: string;
  onCancel: () => void;
  /** Receives the built formula text. */
  onSave: (formulaText: string) => void;
}) {
  const notify = useNotify();
  const [text, setText] = useState(initialText || seedPrefix);
  const [operators, setOperators] = useState<string[]>([]);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  // Re-seed each open: a stored formula shows as-is; an empty one gets the
  // `{target}({unit})=` prefix (legacy only seeds when Formula is empty).
  useEffect(() => {
    if (!open) return;
    setText(initialText && initialText.trim() ? initialText : seedPrefix);
  }, [open, initialText, seedPrefix]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get(operatorsUrl);
        if (!cancelled) setOperators((res.data?.data ?? []) as string[]);
      } catch {
        if (!cancelled) notify.error("Failed to load the operator list.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, operatorsUrl, notify]);

  const operatorRows = useMemo<OperatorRow[]>(
    () => operators.map((operator, i) => ({ id: `${i}-${operator}`, operator })),
    [operators],
  );

  // The legacy grid's leading radio column: it marks the variable picked last. The pick
  // itself is the row click, so the radio is display-only and kept out of the Tab order.
  const [pickedVariable, setPickedVariable] = useState<string | null>(null);
  useEffect(() => {
    if (open) setPickedVariable(null);
  }, [open]);

  const variableColumns = useMemo<AppColumn<ReagentFormulaVariable>[]>(
    () => [
      {
        field: "__pick",
        header: "",
        width: 34,
        align: "center",
        sortable: false,
        filterable: false,
        noExport: true,
        cellClass: () => "rfx-pick-cell",
        render: (v) => (
          <input
            type="radio"
            className="rfx-radio"
            name="rfx-variable"
            tabIndex={-1}
            readOnly
            checked={pickedVariable === v.variable}
            aria-label={`Select ${v.variable}`}
          />
        ),
      },
      { field: "variable", header: "Variable" },
      { field: "unit", header: "Unit" },
    ],
    [pickedVariable],
  );

  /** Append a token, exactly as legacy does — to the END, no separator. */
  const append = (token: string) => {
    setText((t) => t + token);
    areaRef.current?.focus();
  };

  return (
    <AppDialog
      open={open}
      title="Result Correction Formula"
      icon="ti-math-function"
      width={860}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => onSave(text.trim())}>
            OK
          </button>
        </>
      }
    >
      {/* Legacy DashboardView layout: Variable|Unit grid · Operator grid · "Formula:" beside its box. */}
      <div className="rfx-dual">
        <div className="rfx-grid rfx-grid--vars">
          <AppDataGrid<ReagentFormulaVariable>
            rows={variables}
            columns={variableColumns}
            getRowId={(v) => v.variable}
            onRowClick={(v) => {
              setPickedVariable(v.variable);
              append(`${v.variable}(${v.unit})`);
            }}
            defaultPageSize={PICKER_PAGE_SIZE}
            hideFooter
            hideHeaderTools
            fixedLayout
            emptyText="No variables available."
          />
        </div>

        <div className="rfx-grid rfx-grid--ops">
          <AppDataGrid<OperatorRow>
            rows={operatorRows}
            columns={OPERATOR_COLUMNS}
            getRowId={(o) => o.id}
            onRowClick={(o) => append(o.operator)}
            defaultPageSize={PICKER_PAGE_SIZE}
            initialSort={{ key: "operator", dir: "asc" }}
            hideFooter
            hideHeaderTools
            fixedLayout
            autoFocusFirstRow={false}
          />
        </div>

        <div className="rfx-formula">
          <label className="rfx-formula-label" htmlFor="rfx-formula-area">
            Formula:
          </label>
          <textarea
            id="rfx-formula-area"
            ref={areaRef}
            className="rfx-area"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </div>
      <style>{rfxStyles}</style>
    </AppDialog>
  );
}

interface OperatorRow {
  id: string;
  operator: string;
}

/** Both lists are pickers shown whole (the footer is hidden), so no row may fall onto a second page. */
const PICKER_PAGE_SIZE = 500;


const OPERATOR_COLUMNS: AppColumn<OperatorRow>[] = [{ field: "operator", header: "Operator" }];

const rfxStyles = `
  .rfx-dual { display: grid; grid-template-columns: minmax(0, 320px) minmax(0, 170px) minmax(0, 1fr); gap: 20px; align-items: stretch; height: 324px; }
  /* The radio cell drops the grid's cell padding: in a 34px column that padding pushed the radio off-centre. */
  .rfx-grid .adg-table tbody td.rfx-pick-cell { padding: 0; text-align: center; vertical-align: middle; }
  .rfx-radio { display: block; margin: 0 auto; cursor: pointer; accent-color: var(--brand-accent, var(--ai-orange, #e8670a)); }
  .rfx-grid { min-width: 0; height: 100%; display: flex; flex-direction: column; }
  .rfx-grid > .adg { flex: 1 1 auto; min-height: 0; height: 100%; }
  .rfx-grid .adg tbody tr { cursor: pointer; }
  /* No zebra in these two short pick-lists — every row white (owner 2026-10-06). Hover and
     the focus ring are the grid's own and still apply. */
  .rfx-grid .adg-table tbody tr:nth-child(odd),
  .rfx-grid .adg-table tbody tr:nth-child(even) { background: var(--ai-card, #fff); }
  .rfx-formula { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; align-content: start; column-gap: 10px; min-width: 0; }
  .rfx-formula-label { font-size: 12px; font-weight: 600; color: var(--ai-ink, #1f2430); }
  .rfx-area { width: 100%; height: 134px; box-sizing: border-box; border: 1px solid var(--ai-border, #d4d8e0); border-radius: 4px; padding: 6px 8px; font-size: 13px; font-family: inherit; color: var(--ai-ink, #1f2430); resize: vertical; }
  .rfx-area:focus { outline: none; border-color: var(--ai-accent, #e8670a); }
  @media (max-width: 640px) {
    .rfx-dual { grid-template-columns: 1fr; height: auto; }
    .rfx-grid { height: 200px; }
  }
`;
